import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import { useTranslation } from 'react-i18next';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import i18n from 'i18n';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';

interface RateLimitsTabProps {
  rateLimit: number | null;
  rateBurst: number | null;
  disabled?: boolean;
  onUpdateRateLimit: (value: number | null) => void;
  onUpdateRateBurst: (value: number | null) => void;
}

const RateLimitsTab = (props: RateLimitsTabProps) => {
  const { t } = useTranslation('mockServices');

  useEffect(() => {
    const normalizedRateLimit = props.rateLimit ?? '';
    if (rateLimitInput.value !== normalizedRateLimit) {
      rateLimitInput.resetValue(normalizedRateLimit);
    }
    const normalizedRateBurst = props.rateBurst ?? '';
    if (rateBurstInput.value !== normalizedRateBurst) {
      rateBurstInput.resetValue(normalizedRateBurst);
    }
  }, [props.rateLimit, props.rateBurst]);

  const rateLimitInput = useFormInput(
    props.rateLimit ?? '',
    (fn: CallbackFn) => {
      if (fn.isValid) {
        const raw = fn.value;
        if (raw === null || raw === undefined || raw.toString().length === 0) {
          props.onUpdateRateLimit(null);
        } else {
          props.onUpdateRateLimit(parseInt(raw as string, 10));
        }
      }
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: i18n.t('mockServices:parametersTab.rateLimitValidation.numberValidation'),
    },
    {
      type: ValidatorType.MIN,
      value: 1,
      invalidMessage: i18n.t('mockServices:parametersTab.rateLimitValidation.minValidation'),
    },
    {
      type: ValidatorType.MAX,
      value: 2147483647,
      invalidMessage: i18n.t('mockServices:parametersTab.rateLimitValidation.numberValidationRange'),
    },
  );

  const rateBurstInput = useFormInput(
    props.rateBurst ?? '',
    (fn: CallbackFn) => {
      if (fn.isValid) {
        const raw = fn.value;
        if (raw === null || raw === undefined || raw.toString().length === 0) {
          props.onUpdateRateBurst(null);
        } else {
          props.onUpdateRateBurst(parseInt(raw as string, 10));
        }
      }
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: i18n.t('mockServices:parametersTab.rateBurstValidation.numberValidation'),
    },
    {
      type: ValidatorType.MIN,
      value: 1,
      invalidMessage: i18n.t('mockServices:parametersTab.rateBurstValidation.minValidation'),
    },
    {
      type: ValidatorType.MAX,
      value: 2147483647,
      invalidMessage: i18n.t('mockServices:parametersTab.rateBurstValidation.numberValidationRange'),
    },
  );

  return (
    <Form.Group>
      <Form.Label htmlFor={'ms-rate-limit'}>{t('parametersTab.rateLimitLabel')}</Form.Label>
      <InputGroup className="mb-3" style={{ width: '360px' }}>
        <BzFormControlWithHook
          id={'ms-rate-limit'}
          useFormInputHook={rateLimitInput}
          placeholder={t('parametersTab.rateLimitPlaceholder')}
          required={false}
          disabled={props.disabled}
        />
      </InputGroup>

      <Form.Label htmlFor={'ms-rate-burst'}>{t('parametersTab.rateBurstLabel')}</Form.Label>
      <InputGroup className="mb-1" style={{ width: '360px' }}>
        <BzFormControlWithHook
          id={'ms-rate-burst'}
          useFormInputHook={rateBurstInput}
          placeholder={t('parametersTab.rateBurstPlaceholder')}
          required={false}
          disabled={props.disabled}
        />
      </InputGroup>
    </Form.Group>
  );
};

export default RateLimitsTab;
